// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/fenced-imports */
import { useSwiper } from "swiper/react";
/* eslint-enable spire/fenced-imports */

import { ButtonIcon } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import ArrowDown from "@insite/mobius/Icons/ArrowDown";
import ArrowUp from "@insite/mobius/Icons/ArrowUp";
import { getProp } from "@insite/mobius/utilities";
import { ArrowPosition, ArrowType, NavigationProps, useArrowElement } from "@tps/App/Components/XngCarousel";
import IconPaginationArrowLeftLightInactive from "@tps/XDL/Icons/IconPaginationArrowLeftLightInactive";
import IconPaginationArrowRightLightInactive from "@tps/XDL/Icons/IconPaginationArrowRightLightInactive";
import { color, xClassNames } from "@tps/XDL/Utilities";
import React, { FC, useCallback } from "react";
import { css } from "styled-components";

const generateArrowStyle = (arrowPosition: ArrowPosition, withScrollbar?: boolean, disabled?: boolean) => ({
    size: withScrollbar? 24 : 16,
    css: css`
        position: static;
        ${`margin-${arrowPosition === "left" ? "right" : "left"}: 24px`};
        background: ${withScrollbar ? color("primary") : "transparent"};
        & > ${IconWrapper} {
            transition: all ${getProp("theme.transition.duration.regular", "300")}ms ease-in-out;
            color: ${color("common.white")};
            ${!withScrollbar && !disabled && css`
                &:hover {
                    color: ${color("common.ui8")};
                }
            `}
        }
        transform: none;
        z-index: 10;
        padding: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        border: 0;
        outline: none !important;
        ${withScrollbar && css`
            padding: 5px;
            margin: 0;
            ${!disabled && css`
                &:hover {
                    background: ${color("common.ui8")};
                }
            `}
        `}

        &:focus {
            box-shadow: none;
        }

        ${(arrowPosition === "top" || arrowPosition === "bottom") &&
        css`
            position: relative;
            inset: 0;
            transform: none;
            margin: auto;
        `};

        &.swiper-button-disabled {
            ${withScrollbar ? css`
                opacity: 0.33;
                cursor: not-allowed;
            `: css`
                & > ${IconWrapper} {
                    color: ${color("common.ui4", 0.3)};
                    cursor: not-allowed;
                }
            `}
        }
    `,
});

const arrowNavigationIcons = {
    top: {
        src: ArrowUp,
        size: 14,
    },
    bottom: {
        src: ArrowDown,
        size: 14,
    },
    left: {
        src: IconPaginationArrowLeftLightInactive,
        size: 14,
    },
    right: {
        src: IconPaginationArrowRightLightInactive,
        size: 14,
    },
};

const getArrowPosition = (arrowType: ArrowType, direction: "horizontal" | "vertical" = "horizontal"): ArrowPosition => {
    const isVertical = direction === "vertical";
    if (arrowType === "next") {
        return isVertical ? "bottom" : "right";
    }
    // when arrowType='prev'.
    return isVertical ? "top" : "left";
};

const ArrowNavigation: FC<NavigationProps> = React.memo(
    ({ arrowType, showOnMobile, buttonIconProps, withScrollbar, ...otherProps }) => {
        const swiperInstance = useSwiper();
        const arrowPosition = getArrowPosition(arrowType, swiperInstance?.isHorizontal() ? "horizontal" : "vertical");
        const { src: iconSrc, size: iconSize } = arrowNavigationIcons[arrowPosition];

        const {
            className,
            element: arrowElement,
            disabled,
        } = useArrowElement({
            swiper: swiperInstance,
            showOnMobile,
            arrowType,
        });

        const onClickHandler = useCallback(
            (event: any) => {
                arrowElement?.click();
                otherProps.onClick?.(event);
            },
            [arrowElement],
        );

        if (!swiperInstance || !arrowElement) {
            return null;
        }

        const props = {
            arrowPosition,
            showOnMobile,
            className: xClassNames.append(className, otherProps.className),
            withScrollbar,
        };

        return (
            <Clickable
                {...props}
                {...generateArrowStyle(arrowPosition, withScrollbar, disabled)}
                aria-label={`carousel-${arrowPosition}`}
                onClick={onClickHandler}
            >
                <ButtonIcon
                    src={iconSrc}
                    size={iconSize}
                    color="common.white"
                    className={`carousel-${arrowPosition}-icon ${disabled && "disabled"}`}
                    {...buttonIconProps}
                />
            </Clickable>
        );
    },
);

export default ArrowNavigation;
